<template>
    <nav
        class="navigation"
        v-if="$globalConstants.host"
        :style="{
            background: barColor,
            boxShadow: disableBorderShadow ? 'none' : '',
        }"
    >
        <div class="container">
            <div
                class="menu-bars"
                @click="toggleNavMenu"
                :class="{ '--close': mobileMenuOpen }"
            >
                <div class="bars" :style="{ '--primary': burgerIconColor }">
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>

            <div class="logo" @click="mobileMenuOpen = false">
                <router-link :to="{ path: '/' }">
                    <img
                        :src="logoSrc"
                        :width="160"
                        :height="50"
                        alt="logo"
                        fetchpriority="high"
                    />
                </router-link>
            </div>

            <ul
                class="nav-menu"
                :style="{
                    marginRight:
                        !isMobileDevice() &&
                        $globalConstants.host['notification_icon'] &&
                        '46px',
                }"
            >
                <li
                    class="nav-item"
                    data-test-id="nav-item"
                    v-for="item in navItems"
                    :key="item.name"
                    :style="{ 'border-color': underlineColor }"
                >
                    <template v-if="item.target && item.target === '_blank'">
                        <a
                            v-if="!$globalConstants.host.goto_tags"
                            class="nav-link"
                            :style="{ color: underlineColor }"
                            :href="attachTrackingParams(item.route)"
                            rel="noopener noreferrer nofollow"
                            target="_blank"
                        >
                            {{ $t(item.name) }}
                        </a>
                    </template>

                    <router-link
                        class="nav-link"
                        :style="{ color: underlineColor }"
                        :to="{ path: item.route }"
                        v-else-if="!item.dropdown_items"
                    >
                        {{ $t(item.name) }}
                    </router-link>

                    <div
                        v-else
                        class="nav-link"
                        :style="{ color: underlineColor }"
                    >
                        {{ $t(item.name) }}
                        <svg
                            class="icon chevron"
                            width="1em"
                            height="1em"
                            viewBox="0 0 1200 1200"
                        >
                            <path
                                d="M600.006 989.352l178.709-178.709L1200 389.357l-178.732-178.709L600.006 631.91L178.721 210.648L0 389.369l421.262 421.262l178.721 178.721h.023z"
                                :fill="underlineColor"
                            />
                        </svg>
                    </div>

                    <transition name="fade">
                        <ul
                            class="nav-item-dropdown"
                            v-if="item.dropdown_items"
                        >
                            <li
                                class="dropdown-item"
                                v-for="dropdown_item in item.dropdown_items"
                                :key="dropdown_item.name"
                            >
                                <router-link :to="{ path: dropdown_item.route }"
                                    >{{ dropdown_item.name }}
                                </router-link>
                            </li>
                        </ul>
                    </transition>
                </li>
            </ul>

            <SearchBar
                v-if="$globalConstants.host['adsense']"
                @toggleMenuItems="toggleMenuItems()"
            ></SearchBar>

            <NotificationBell
                v-if="$globalConstants.host['notification_icon'] && !isMobile()"
            />
        </div>
        <div
            class="container rating-disclosure__container"
            @click="toggleRatingDisclosure"
        >
            <div class="rating-disclosure__link">
                {{ $t('How do we rate?') }}
            </div>
            <Portal to="rating-disclosure" v-if="ratingInfoOpen">
                <div
                    class="rating-disclosure"
                    @click.self="closeRatingDisclosure"
                >
                    <div class="rating-disclosure__card">
                        <div
                            class="rating-disclosure__close"
                            @click="toggleRatingDisclosure"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                class="size-6"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M6 18 18 6M6 6l12 12"
                                />
                            </svg>
                        </div>
                        <div class="rating-disclosure__title">
                            {{ $t('How do we rate?') }}
                        </div>
                        <div
                            v-html="
                                $globalConstants.host['domain_id'] === 2
                                    ? $t('Rating info new')
                                    : this.$t('Rating info text')
                            "
                        ></div>
                    </div>
                </div>
            </Portal>
        </div>

        <mobile-menu
            v-if="mobileMenuOpen && isMobileDevice()"
            :navItems="navItems"
            :backgroundColor="mobileBackgroundColor"
            @close-menu="mobileMenuOpen = !mobileMenuOpen"
        ></mobile-menu>
    </nav>
</template>

<script>
import {
    colorMixin,
    helper,
    mediaQueriesMixin,
    navigationItemsMixin,
} from '@/mixins';

export default {
    name: 'DefaultNav',
    mixins: [navigationItemsMixin, mediaQueriesMixin, helper, colorMixin],
    data() {
        const header_banner = this.$globalConstants.host.header_banner;
        return {
            logoSrc: this.resizeImage(header_banner, 160, 50, 100),
            ratingInfoOpen: false,
        };
    },
    props: {
        barColor: {
            type: String,
            default: 'white',
        },
        mobileBackgroundColor: {
            type: String,
            default: 'black',
        },
        burgerIconColor: {
            type: String,
            default: 'black',
        },
        underlineColor: {
            type: String,
            default: '',
        },
        disableBorderShadow: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        toggleRatingDisclosure() {
            this.ratingInfoOpen = !this.ratingInfoOpen;
            this.ratingDisclosureEvent(this.ratingInfoOpen);
        },
        closeRatingDisclosure() {
            if (!this.ratingInfoOpen) return;

            this.ratingInfoOpen = false;
            this.ratingDisclosureEvent(false);
        },
        ratingDisclosureEvent(toggleValue) {
            if (!window.track) return;
            window.track.event({
                event: 'rating_disclosure',
                action: toggleValue === true ? 'open' : 'close',
            });
        },
    },
    components: {
        MobileMenu: () =>
            import(/* webpackChunkName: "MobileMenu" */ './MobileMenu'),
        NotificationBell: () =>
            import(
                /* webpackChunkName: "NotificationBell" */ './NotificationBell'
            ),
        SearchBar: () =>
            import(
                /* webpackChunkName: "SearchBar" */ '@molecules/SearchBar/SearchBar'
            ),
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';

.navigation {
    width: 100%;
    height: 50px;
    display: flex;
    justify-content: center;
    align-content: center;
    background: white;
    z-index: 60;
    position: relative;
    box-shadow: 4px 4px 10px hsl(0deg 0% 77% / 25%);

    .container {
        width: 70%;
        height: 100%;
        max-width: 1180px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        transition: border 0.3s ease;
        padding: 0 0 0 20px;
        @include mq('tablet-wide') {
            padding: 0 0 0 40px;
            width: 100%;
        }

        .logo {
            position: absolute;
            left: 60%;
            transform: translateX(-50%);
            display: flex;
            justify-content: center;
            height: 50px;
            width: 160px;

            @include mq('tablet-wide') {
                position: relative;
                justify-content: flex-start;
                left: 0;
                transform: translateX(0);
                width: 230px;
            }

            a {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                width: 100%;
                aspect-ratio: 160/50;

                @include mq('tablet') {
                    aspect-ratio: 230/50;
                }

                img {
                    object-fit: contain;
                    height: 100%;
                    width: 100%;
                }
            }
        }

        .nav-menu {
            display: none;
            list-style-type: none;
            height: 100%;
            @include mq('tablet-wide') {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                grid-column-gap: 14px;
            }

            > .nav-item {
                display: inline-flex;
                cursor: pointer;
                position: relative;
                height: 100%;

                &.--toggleDropdown {
                    > .nav-item-dropdown {
                        display: flex;
                    }
                }

                &:hover {
                    > .nav-item-dropdown {
                        transition: opacity 0.3s ease;
                        transform: scale(1);
                        opacity: 1;
                    }

                    > .nav-link {
                        color: rgba(#333, 1);
                        border-color: inherit;
                    }
                }

                > .nav-link {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-decoration: none;
                    color: rgba(#333, 0.9);
                    font-size: 12px;
                    letter-spacing: 0.005em;
                    text-align: center;
                    font-weight: 500;
                    text-transform: uppercase;
                    line-height: 14px;
                    border-bottom: 4px solid transparent;
                    padding: 0 8px;
                    margin-top: 4px;

                    > .chevron {
                        margin-left: 10px;
                        width: 8px;
                    }
                }

                > .nav-item-dropdown {
                    transform: scale(0.00001);
                    opacity: 0;
                    top: 50px;
                    min-width: fit-content;
                    width: 100%;
                    left: 0;
                    z-index: 3;
                    position: absolute;
                    list-style-type: none;
                    padding: 0;

                    > li {
                        background-color: rgba(#000, 1);
                        transition: background-color 0.3s ease-out;

                        &:hover {
                            a {
                                color: rgba(255, 255, 255, 1);
                                text-decoration: underline;
                                font-weight: bold;
                            }
                        }

                        > a {
                            transition: color 0.3s ease-out;
                            display: inline-block;
                            font-weight: 500;
                            font-size: 12px;
                            line-height: 14px;
                            letter-spacing: 0.15px;
                            text-decoration: none;
                            color: rgba(255, 255, 255, 0.7);
                            padding: 1em 1.5em;
                        }
                    }
                }
            }
        }

        .menu-bars {
            top: 50%;
            bottom: 50%;
            display: flex;
            width: 46px;
            align-items: center;
            justify-content: center;
            @include mq('tablet-wide') {
                display: none;
            }

            > .bars {
                position: relative;
                height: 20px;
                width: 100%;
                display: flex;
                justify-content: space-between;
                flex-direction: column;

                div {
                    background: var(--primary);
                    position: absolute;
                    height: 4px;
                    width: 26px;
                    border-radius: 3px;

                    &:first-of-type {
                        top: 0;
                    }

                    &:nth-of-type(2) {
                        top: 50%;
                        transform: translateY(-50%);
                    }

                    &:last-of-type {
                        bottom: 0;
                    }
                }
            }

            &.--close .bars {
                div:first-of-type {
                    transform: translateY(-50%) rotate(-135deg);
                    top: 50%;
                    position: relative;
                }

                div:nth-of-type(2) {
                    opacity: 0;
                }

                div:last-of-type {
                    transform: translateY(50%) rotate(135deg);
                    bottom: 50%;
                    position: relative;
                }
            }
        }

        .notification-bell {
            position: absolute;
            right: 46px;
        }
    }
}

.rating-disclosure {
    position: fixed;
    top: -10px;
    left: 0;
    width: 100%;
    height: calc(100% + 10px);
    z-index: 30;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 20px;
    overflow: scroll;
    backdrop-filter: blur(3px);
    background: rgba(0, 0, 0, 0.5);
    scrollbar-width: none; // Firefox
    &::-webkit-scrollbar {
        display: none;
    }

    &__container {
        padding: 0 !important;
        width: auto !important;
        flex: 1 0 40%;

        @include mq('tablet') {
            flex: 1 0 20%;
        }
        @include mq('tablet-wide') {
            flex: unset;
        }
    }

    &__link {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        color: rgba(51, 51, 51, 0.9);
        font-size: 12px;
        letter-spacing: 0.005em;
        text-align: center;
        font-weight: 500;
        text-transform: uppercase;
        line-height: 14px;
        border-bottom: 4px solid transparent;
        padding: 0 8px;
        margin-top: 4px;
        height: 100%;
        cursor: pointer;

        &:hover {
            color: rgba(#333, 1);
            border-color: inherit;
        }
    }

    &__card {
        background: white;
        border-radius: 10px;
        padding: 24px;
        max-width: 360px;
        width: 100%;
        margin: 50px auto 0 auto;
        font-size: 14px;
        line-height: 20px;
        text-align: center;
        font-weight: 500;
        display: flex;
        flex-direction: column;
        gap: 16px;
        color: #898989;
        position: relative;

        @include mq('tablet') {
            max-width: 650px;
            font-size: 16px;
        }
    }

    &__title {
        font-size: 18px;
        line-height: 24px;
        font-weight: 600;
        color: #333;
        margin-top: 10px;

        @include mq('tablet') {
            font-size: 20px;
        }
    }

    &__close {
        position: absolute;
        top: 10px;
        right: 10px;
        cursor: pointer;
        width: 24px;
        height: 24px;
        color: #898989;

        &:hover {
            color: #333;
        }
    }
}
</style>
